import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, DollarSign, Clock } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['nails', 'spray_tan', 'facial', 'hair', 'makeup', 'addon', 'other'];

const blank = { name: '', category: 'nails', description: '', price: '', duration_minutes: 60, active: true };

const ServiceMenu = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or service object
  const [form, setForm] = useState(blank);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/services`);
      setServices(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(blank); setEditing({}); };
  const startEdit = (s) => { setForm({ ...s, price: String(s.price) }); setEditing(s); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      description: form.description || null,
      price: parseFloat(form.price),
      duration_minutes: parseInt(form.duration_minutes, 10) || 60,
      active: form.active,
    };
    if (editing && editing.id) {
      await axios.put(`${API_URL}/api/services/${editing.id}`, payload);
    } else {
      await axios.post(`${API_URL}/api/services`, payload);
    }
    setEditing(null);
    setForm(blank);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await axios.delete(`${API_URL}/api/services/${id}`);
    load();
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading menu...</div>;

  const grouped = services.reduce((acc, s) => {
    acc[s.category] = acc[s.category] || [];
    acc[s.category].push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Service Menu</h1>
          <p className="text-sm text-muted-foreground mt-1">Your services & pricing — one source of truth.</p>
        </div>
        <Button onClick={startNew} data-testid="service-add-btn"><Plus className="w-4 h-4 mr-2" /> Add Service</Button>
      </div>

      {services.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">No services yet. Load the default menu or add your own.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await axios.post(`${API_URL}/api/services/seed-defaults`);
                  await load();
                } catch (e) { console.error(e); }
              }}
              data-testid="seed-defaults-btn"
            >
              ✨ Load Default Menu (18 services)
            </Button>
            <Button onClick={startNew}><Plus className="w-4 h-4 mr-2" /> Add Service</Button>
          </div>
        </Card>
      ) : (
        Object.keys(grouped).sort().map(cat => (
          <div key={cat} data-testid={`category-${cat}`}>
            <h2 className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-3">{cat.replace('_', ' ')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {grouped[cat].map(s => (
                <Card key={s.id} className={`p-5 ${!s.active ? 'opacity-50' : ''}`} data-testid={`service-${s.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold">{s.name}</h3>
                      {s.description && <p className="text-xs text-muted-foreground mt-1">{s.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(s)} className="p-1 hover:bg-accent rounded" data-testid={`edit-${s.id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(s.id)} className="p-1 hover:bg-accent rounded text-red-500" data-testid={`del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-4 text-sm">
                    <span className="flex items-center gap-1 text-emerald-600 font-medium"><DollarSign className="w-4 h-4" />{s.price.toFixed(2)}</span>
                    <span className="flex items-center gap-1 text-muted-foreground"><Clock className="w-3 h-3" />{s.duration_minutes}m</span>
                    {!s.active && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Editor modal */}
      {editing !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <Card className="p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="service-form">
            <h3 className="font-semibold text-lg mb-4">{editing.id ? 'Edit Service' : 'New Service'}</h3>
            <form onSubmit={submit} className="space-y-3">
              <Input placeholder="Service name (e.g., Acrylic Full Set)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="svc-name" />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded-md px-3 py-2 bg-background capitalize" data-testid="svc-category">
                {CATEGORIES.map(c => <option key={c} value={c} className="capitalize">{c.replace('_', ' ')}</option>)}
              </select>
              <Input placeholder="Short description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="svc-desc" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" step="0.01" placeholder="Price ($)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required data-testid="svc-price" />
                <Input type="number" placeholder="Duration (min)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} required data-testid="svc-duration" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="svc-active" />
                Active (visible on booking)
              </label>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1" data-testid="svc-submit">Save</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ServiceMenu;
